/**
 * Number/unit formatting — spec §F2 (mono numbers, em-dash for missing values).
 * Ported from the mockup's `fmt(v, d = 1)`: `toLocaleString` gives the thousands
 * separators, `—` stands in for null/NaN so tables never show "NaN".
 *
 * **The locale is the interface language** (§F12), not a hardcoded `'en'`. French groups
 * thousands with a narrow no-break space and marks decimals with a comma, so `97,967.5` in
 * English is `97 967,5` in French — and a French page printing `97,967.5` reads as
 * ninety-seven point nine, which is a factor of a thousand, not a style preference. Read at
 * call time rather than captured, because the toggle changes it mid-session.
 */
import i18n, { DEFAULT_LANGUAGE } from './i18n';

export const NO_VALUE = '-';

/**
 * The separator between a label and what it introduces: ": " in English, and in French a
 * narrow no-break space before the colon, as French typography sets it. Read at call time,
 * like `locale()`, because the language toggle changes it mid-session.
 */
export function colonSep(): string {
  return locale().startsWith('fr') ? '\u202f: ' : ': ';
}

/** The active interface language as a BCP 47 tag for `toLocaleString`. */
const locale = (): string => i18n.language || DEFAULT_LANGUAGE;

/**
 * Format a number for display. `decimals` is a MAXIMUM: 1048 -> "1,048",
 * 12.3456 with 2 -> "12.35". Non-finite / null / undefined -> em dash.
 */
export function fmtNum(v: number | null | undefined, decimals = 1): string {
  if (v === null || v === undefined) return NO_VALUE;
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return NO_VALUE;
  return n.toLocaleString(locale(), {
    maximumFractionDigits: decimals,
    minimumFractionDigits: 0,
  });
}

/**
 * Format a number to **exactly** `decimals` places — `fmtNum`'s maximum made a minimum.
 *
 * The difference matters wherever a column of numbers is read against itself: UC2's
 * practice differences print as "+1.4 points" and "−4.0 points", and dropping the trailing
 * zero on the second would make two numbers of the same precision look like two numbers of
 * different precision. Same locale rule as `fmtNum` (§F12), read at call time.
 */
export function fmtFixed(v: number | null | undefined, decimals = 1): string {
  if (v === null || v === undefined) return NO_VALUE;
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return NO_VALUE;
  return n.toLocaleString(locale(), {
    maximumFractionDigits: decimals,
    minimumFractionDigits: decimals,
  });
}

/** Areas: km² with a non-breaking space (U+00A0) before the unit, so it never wraps. */
export function fmtKm2(v: number | null | undefined, decimals = 0): string {
  const s = fmtNum(v, decimals);
  return s === NO_VALUE ? s : `${s}\u00a0km²`;
}

/** Compact counts for stat tiles: 1_048 -> "1,048", 12_400 -> "12.4k", 3_200_000 -> "3.2M". */
export function fmtCompact(v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(Number(v))) return NO_VALUE;
  const n = Number(v);
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${fmtNum(n / 1_000_000, 1)}M`;
  if (abs >= 10_000) return `${fmtNum(n / 1_000, 1)}k`;
  return fmtNum(n, 0);
}

/**
 * Tile numbers — the Analysis panel's stat tiles, which have one line and must never wrap
 * mid-digit (client round 2 Oct 2026: "4,541,5 12 ha" across two lines in a 96 px tile).
 *
 * `4_541_512 -> "4.54 M"`, `128_000 -> "128 k"`, `541 -> "541"`, and in French
 * `"4,54 M"` / `"128 k"` — the digits go through `fmtNum`, so the locale's own separators
 * and decimal mark apply (§F12), and the magnitude letter is joined with a **non-breaking
 * space** so the number and its suffix stay one word whatever the tile's width.
 *
 * Distinct from `fmtCompact`, which is the insights panel's tighter `12.4k`/`3.2M` with no
 * space: a 16 px raster-statistics figure beside its own label, not a headline number.
 */
export function fmtCompactNum(v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(Number(v))) return NO_VALUE;
  const n = Number(v);
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${fmtNum(n / 1_000_000, 2)}\u00a0M`;
  if (abs >= 10_000) return `${fmtNum(n / 1_000, 0)}\u00a0k`;
  return fmtNum(n, 0);
}

/** Percentages, e.g. 0.7812 -> "78%" (pass `fraction: false` for values already 0-100). */
export function fmtPct(v: number | null | undefined, decimals = 0, fraction = true): string {
  if (v === null || v === undefined || !Number.isFinite(Number(v))) return NO_VALUE;
  const n = fraction ? Number(v) * 100 : Number(v);
  return `${fmtNum(n, decimals)}%`;
}
